import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import './Home.css';

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDuration(startIso) {
  if (!startIso) return '0h 0m';
  const diff = Math.floor((Date.now() - new Date(startIso)) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return `${h}h ${m}m`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function getFirstName(fullName) {
  if (!fullName) return '';
  return fullName.trim().split(' ')[0];
}

function getInitials(fullName) {
  if (!fullName) return '?';
  return fullName.trim().split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function calculateStreak(records) {
  if (!records || records.length === 0) return 0;
  const sorted = [...records]
    .filter(r => r.clock_in)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  let streak = 0;
  for (const r of sorted) {
    if (r.late_minutes === 0) streak++;
    else break;
  }
  return streak;
}

function calculatePerformance(records) {
  if (!records || records.length === 0) return 0;
  const totalDays    = records.length;
  const presentDays  = records.filter(r => r.clock_in).length;
  const lateDays     = records.filter(r => r.late_minutes > 0).length;
  const absentDays   = records.filter(r => !r.clock_in).length;
  const attendanceScore  = totalDays > 0 ? (presentDays / totalDays) * 50 : 0;
  const punctualityScore = presentDays > 0 ? ((presentDays - lateDays) / presentDays) * 35 : 0;
  const absenceBonus     = absentDays === 0 ? 15 : Math.max(0, 15 - absentDays * 3);
  return Math.min(100, Math.round(attendanceScore + punctualityScore + absenceBonus));
}

function getPerformanceLabel(score) {
  if (score >= 90) return { label: 'Excellent', color: '#22c55e', emoji: '⭐' };
  if (score >= 75) return { label: 'Good',      color: '#3b82f6', emoji: '👍' };
  if (score >= 60) return { label: 'Average',   color: '#f59e0b', emoji: '📈' };
  return              { label: 'Needs Work',  color: '#ef4444', emoji: '⚠️' };
}

function AnimNum({ value, duration = 600 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (end === 0) { setDisplay(0); return; }
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, end);
      setDisplay(start);
      if (start >= end) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
}

function AttendanceRing({ rate, present, lates, absent }) {
  const r     = 38;
  const circ  = 2 * Math.PI * r;
  const gap   = 2;
  const total = present + lates + absent;
  const pArc  = total > 0 ? Math.max(0, (present / total) * circ - gap) : 0;
  const lArc  = total > 0 ? Math.max(0, (lates   / total) * circ - gap) : 0;
  const aArc  = total > 0 ? Math.max(0, (absent  / total) * circ - gap) : 0;
  const pOff  = circ;
  const lOff  = circ - (total > 0 ? (present / total) * circ : 0);
  const aOff  = lOff - (total > 0 ? (lates   / total) * circ : 0);
  return (
    <div className="home-ring-box">
      <svg width="96" height="96" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#f1f5f9" strokeWidth="9" />
        {present > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#22c55e" strokeWidth="9" strokeDasharray={`${pArc} ${circ}`} strokeDashoffset={pOff} transform="rotate(-90 50 50)" />}
        {lates   > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#f59e0b" strokeWidth="9" strokeDasharray={`${lArc} ${circ}`} strokeDashoffset={lOff} transform="rotate(-90 50 50)" />}
        {absent  > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke="#ef4444" strokeWidth="9" strokeDasharray={`${aArc} ${circ}`} strokeDashoffset={aOff} transform="rotate(-90 50 50)" />}
        <text x="50" y="45" textAnchor="middle" fontSize="16" fontWeight="800" fill="#0f172a">{rate}%</text>
        <text x="50" y="57" textAnchor="middle" fontSize="8" fill="#94a3b8">RATE</text>
      </svg>
      <div className="home-ring-legend">
        {present > 0 && <span className="rleg rleg-green">●{present}P</span>}
        {lates   > 0 && <span className="rleg rleg-amber">●{lates}L</span>}
        {absent  > 0 && <span className="rleg rleg-red">●{absent}A</span>}
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`home-toast home-toast-${type}`}>
      <span className="home-toast-icon">{type === 'success' ? '✓' : type === 'error' ? '✗' : '⚠'}</span>
      <span>{message}</span>
    </div>
  );
}

export default function Home({ employee }) {
  const [clockedIn, setClockedIn]       = useState(false);
  const [currentTime, setCurrentTime]   = useState('');
  const [attendance, setAttendance]     = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [todayRecord, setTodayRecord]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [duration, setDuration]         = useState('');
  const [toast, setToast]               = useState(null);
  const [shiftEnd, setShiftEnd]         = useState(null); // shift end time "HH:MM"
  const [clockOutWarning, setClockOutWarning] = useState(null); // warning level 1-5
  const durationRef                     = useRef(null);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();
  // Use PKT timezone for date comparison to match server
  // Bulletproof PKT date — UTC+5
  const getPKTDate = () => {
    const n = new Date();
    const utc = n.getTime() + n.getTimezoneOffset() * 60000;
    const pkt = new Date(utc + 5 * 3600000);
    return pkt.toISOString().split('T')[0];
  };
  const today = getPKTDate();

  useEffect(() => {
    const tick = () => setCurrentTime(new Date().toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    clearInterval(durationRef.current);
    if (todayRecord?.clock_in && !todayRecord?.clock_out) {
      setDuration(formatDuration(todayRecord.clock_in));
      durationRef.current = setInterval(() => setDuration(formatDuration(todayRecord.clock_in)), 1000);
    }
    return () => clearInterval(durationRef.current);
  }, [todayRecord]);

  // ── Clock-out warning system ──────────────────────────
  useEffect(() => {
    if (!shiftEnd || !todayRecord?.clock_in || todayRecord?.clock_out) {
      setClockOutWarning(null);
      return;
    }

    const checkWarning = () => {
      const now = new Date();
      const [endH, endM] = shiftEnd.split(':').map(Number);
      const shiftEndMins = endH * 60 + endM;
      const currentMins  = now.getHours() * 60 + now.getMinutes();
      const minsLeft     = shiftEndMins - currentMins;

      if      (minsLeft <= 0)  setClockOutWarning(5); // AT/PAST shift end
      else if (minsLeft <= 5)  setClockOutWarning(4);
      else if (minsLeft <= 15) setClockOutWarning(3);
      else if (minsLeft <= 30) setClockOutWarning(2);
      else if (minsLeft <= 60) setClockOutWarning(1);
      else                     setClockOutWarning(null);
    };

    checkWarning();
    const t = setInterval(checkWarning, 60000); // check every minute
    return () => clearInterval(t);
  }, [shiftEnd, todayRecord]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async (background = false) => {
    try {
      if (!background) setLoading(true);
      const [attData, lvData, profileData] = await Promise.all([
        api.getMyAttendance(month, year),
        api.getLeaveBalance(),
        api.getMyProfile(),
      ]);
      // Store shift end time for clock-out warnings
      if (profileData?.end_time) {
        setShiftEnd(profileData.end_time.substring(0, 5)); // "HH:MM"
      }
      setAttendance(attData);
      setLeaveBalance(lvData.balance || null);

      const currentToday = getPKTDate();
      const todayRec = attData.records?.find(r => {
        if (!r.date && !r.date_str) return false;
        const d = r.date_str || r.date;
        return d.substring(0, 10) === currentToday;
      });

      if (todayRec) {
        // Found the record — always update
        setTodayRecord(todayRec);
        setClockedIn(!!(todayRec.clock_in && !todayRec.clock_out));
      } else if (!background) {
        // Only clear state if this is NOT a background refresh after clock-in
        setTodayRecord(null);
        setClockedIn(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!background) setLoading(false);
    }
  };

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  const getFreshLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('no-gps')); return; }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true, timeout: 15000, maximumAge: 0,
    });
  });

  const handleClock = async () => {
    if (clockLoading) return;
    setClockLoading(true);
    try {
      if (clockedIn) {
        const res = await api.clockOut();
        if (res.error) { showToast(res.error, 'error'); return; }
        showToast(`Clocked out · ${res.total_hours} total`, 'success');
        setTodayRecord(prev => prev ? { ...prev, clock_out: new Date().toISOString(), total_minutes: res.total_minutes || 0 } : prev);
        setClockedIn(false);
        loadAll();
      } else {
        let pos;
        try { pos = await getFreshLocation(); }
        catch (e) {
          if (e.code === 1) showToast('Location permission denied — enable GPS', 'error');
          else if (e.code === 3) showToast('GPS timed out — try near a window', 'error');
          else showToast('Could not get location — try again', 'error');
          return;
        }
        const res = await api.clockIn(pos.coords.latitude, pos.coords.longitude, null, null);
        if (res.error) {
          if (res.error.includes('Already clocked in')) {
            // Server confirmed they're clocked in — update state immediately
            setClockedIn(true);
            showToast('You are already clocked in today', 'warning');
            loadAll(true);
          } else {
            showToast(res.reasons?.[0] || res.error, 'error');
          }
          return;
        }
        const lateMsg = res.late_minutes > 0 ? ` · ${res.late_minutes}m late` : ' · On time ✓';
        showToast(`Clocked in at ${formatTime(new Date())}${lateMsg}`, res.late_minutes > 0 ? 'warning' : 'success');
        // Immediately update state so button switches instantly
        setTodayRecord({
          clock_in: new Date().toISOString(),
          clock_out: null,
          late_minutes: res.late_minutes || 0,
          status: res.status || 'present',
          date: today,
        });
        setClockedIn(true);
        loadAll(true);
      }
    } catch { showToast('Something went wrong — try again', 'error'); }
    finally { setClockLoading(false); }
  };

  const summary        = attendance?.summary || {};
  const records        = attendance?.records || [];
  const recent         = records.slice(0, 5);
  const presentDays    = summary.present || 0;
  const absentDays     = summary.absent  || 0;
  const totalTracked   = presentDays + absentDays;
  const attendanceRate = totalTracked > 0 ? Math.round((presentDays / totalTracked) * 100) : 0;
  const streak         = calculateStreak(records);
  const perfScore      = calculatePerformance(records);
  const perfLabel      = getPerformanceLabel(perfScore);
  const isLateToday    = todayRecord?.late_minutes > 0;

  const leaves = leaveBalance ? [
    { type: 'Casual', remaining: leaveBalance.casual?.remaining ?? 3,  total: leaveBalance.casual?.total ?? 3,  color: '#ef4444' },
    { type: 'Sick',   remaining: leaveBalance.sick?.remaining   ?? 5,  total: leaveBalance.sick?.total   ?? 5,  color: '#22c55e' },
    { type: 'Annual', remaining: leaveBalance.annual?.remaining ?? 12, total: leaveBalance.annual?.total ?? 12, color: '#3b82f6' },
  ] : [];

  return (
    <div className="home-root">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="home-hero">
        <div className="home-greeting-row">
          <div className="home-avatar">{getInitials(employee?.full_name)}</div>
          <div className="home-greeting-text">
            <span className="home-greeting">{getGreeting()},</span>
            <span className="home-name">{getFirstName(employee?.full_name)} 👋</span>
          </div>
          {streak > 0 && (
            <div className="home-streak-badge">🔥 {streak} day{streak > 1 ? 's' : ''}</div>
          )}
        </div>

        <div className="home-clock-wrap">
          <div className="home-clock-time">
            <span className="home-clock-hm">{currentTime?.replace(/\s?(am|pm)/i, '')}</span>
            <span className="home-clock-period">{currentTime?.match(/am|pm/i)?.[0]?.toUpperCase() || ''}</span>
          </div>
          <div className="home-clock-date">
            {now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        <div className="home-status-row">
          {clockedIn ? (
            <div className="home-status-chip home-chip-in">
              <span className="home-status-pulse" />
              In since {formatTime(todayRecord?.clock_in)} · {duration}
            </div>
          ) : todayRecord?.clock_out ? (
            <div className="home-status-chip home-chip-done">
              ✓ Completed · {Math.floor((todayRecord.total_minutes||0)/60)}h {(todayRecord.total_minutes||0)%60}m
            </div>
          ) : (
            <div className="home-status-chip home-chip-out">○ Not clocked in yet</div>
          )}
          {isLateToday && (
            <div className="home-late-chip">⏱ {todayRecord.late_minutes}m late</div>
          )}
        </div>

        {!todayRecord?.clock_out && (
          <button
            className={`home-clock-btn ${clockedIn ? 'home-btn-out' : 'home-btn-in'} ${clockLoading ? 'home-btn-loading' : ''}`}
            onClick={handleClock} disabled={clockLoading}
          >
            {clockLoading ? 'Please wait…' : clockedIn ? 'Clock Out' : 'Clock In'}
          </button>
        )}

        {todayRecord?.clock_out && (
          <div className="home-done-msg">✓ You've completed your shift today</div>
        )}

        {/* Clock-out warnings */}
        {clockOutWarning && !todayRecord?.clock_out && (
          <div className={`home-clockout-warning home-cow-${clockOutWarning}`}>
            <span className="home-cow-icon">
              {clockOutWarning === 5 ? '🚨' :
               clockOutWarning === 4 ? '🔴' : '⚠️'}
            </span>
            <div className="home-cow-text">
              <div className="home-cow-title">
                {clockOutWarning === 5 ? 'SHIFT ENDED — Clock out immediately!' :
                 clockOutWarning === 4 ? `5 minutes left — Clock out soon!` :
                 clockOutWarning === 3 ? `15 minutes left — Don't forget to clock out` :
                 clockOutWarning === 2 ? `30 minutes left — Reminder to clock out` :
                                         `1 hour left — Your shift ends soon`}
              </div>
              <div className="home-cow-sub">
                Warning {clockOutWarning}/5 — Failure to clock out may result in auto clock-out
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="home-content">

        {isLateToday && (
          <div className="home-notice">
            <span>⏱</span>
            <div>
              <div className="home-notice-title">Late arrival recorded</div>
              <div className="home-notice-sub">
                {todayRecord.late_minutes} minutes late today.
                {todayRecord.late_minutes >= 180 ? ' Full day deduction may apply.' :
                 todayRecord.late_minutes >= 60  ? ' Half day deduction risk.' : ''}
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="home-perf-row">
            <div className="home-perf-card">
              <div className="home-perf-top">
                <span className="home-perf-emoji">{perfLabel.emoji}</span>
                <span className="home-perf-label" style={{ color: perfLabel.color }}>{perfLabel.label}</span>
              </div>
              <div className="home-perf-score" style={{ color: perfLabel.color }}>
                <AnimNum value={perfScore} />
                <span className="home-perf-unit">/100</span>
              </div>
              <div className="home-perf-sub">Performance Score</div>
              <div className="home-perf-bar">
                <div className="home-perf-fill" style={{ width: `${perfScore}%`, background: perfLabel.color }} />
              </div>
            </div>
            <div className="home-streak-card">
              <div className="home-streak-fire">{streak > 0 ? '🔥' : '❄️'}</div>
              <div className="home-streak-num"><AnimNum value={streak} /></div>
              <div className="home-streak-lbl">Day{streak !== 1 ? 's' : ''} On Time</div>
              <div className="home-streak-sub">
                {streak === 0 ? 'Start your streak!' :
                 streak < 5  ? 'Keep it up!' :
                 streak < 10 ? 'Great streak! 💪' : 'Unstoppable! 🏆'}
              </div>
            </div>
          </div>
        )}

        <div className="home-section-label">{now.toLocaleString('default', { month: 'long' })} {year}</div>

        {loading ? (
          <div className="home-loading">Loading your data…</div>
        ) : (
          <>
            <div className="home-stats-row">
              <div className="home-stats-grid">
                <div className="home-stat home-stat-green">
                  <span className="home-stat-num"><AnimNum value={presentDays} /></span>
                  <span className="home-stat-lbl">Present</span>
                </div>
                <div className="home-stat home-stat-red">
                  <span className="home-stat-num"><AnimNum value={absentDays} /></span>
                  <span className="home-stat-lbl">Absent</span>
                </div>
                <div className="home-stat home-stat-amber">
                  <span className="home-stat-num"><AnimNum value={summary.lates || 0} /></span>
                  <span className="home-stat-lbl">Lates</span>
                </div>
              </div>
              <div className="home-ring-wrap">
                <AttendanceRing rate={attendanceRate} present={presentDays} lates={summary.lates || 0} absent={absentDays} />
              </div>
            </div>

            {leaves.length > 0 && (
              <>
                <div className="home-section-label">Leave Balance</div>
                <div className="home-card">
                  {leaves.map((l, i) => (
                    <div key={i} className="home-leave-item">
                      <div className="home-leave-top">
                        <span className="home-leave-type">{l.type} Leave</span>
                        <span className="home-leave-count" style={{ color: l.color }}>{l.remaining} / {l.total} days</span>
                      </div>
                      <div className="home-leave-bar">
                        <div className="home-leave-fill" style={{ width: `${Math.max(0, (l.remaining / l.total) * 100)}%`, background: l.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="home-section-label">Recent Activity</div>
            <div className="home-card">
              {recent.length === 0 ? (
                <div className="home-empty">No records this month</div>
              ) : recent.map((r, i) => {
                const hasIn   = !!r.clock_in;
                const isLate  = hasIn && r.late_minutes > 0;
                const ongoing = hasIn && !r.clock_out;
                const iconCls  = !hasIn ? 'absent' : isLate ? 'late' : 'present';
                const icon     = !hasIn ? '✗' : isLate ? '⏱' : '✓';
                const badgeCls = !hasIn ? 'babsent' : isLate ? 'blate' : 'bpresent';
                const badgeLbl = !hasIn ? 'Absent' : isLate ? 'Late' : 'Present';
                return (
                  <div key={i} className="home-act-item">
                    <div className={`home-act-icon ${iconCls}`}>{icon}</div>
                    <div className="home-act-info">
                      <div className="home-act-date">
                        {new Date(r.date).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {isLate && <span className="home-act-late">+{r.late_minutes}m</span>}
                      </div>
                      <div className="home-act-times">
                        {formatTime(r.clock_in)} → {ongoing ? 'ongoing' : formatTime(r.clock_out)}
                      </div>
                    </div>
                    <div className="home-act-right">
                      <div className="home-act-hours">
                        {ongoing ? duration : r.total_minutes ? `${Math.floor(r.total_minutes/60)}h ${r.total_minutes%60}m` : '—'}
                      </div>
                      <span className={`badge ${badgeCls}`}>{badgeLbl}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}